// =============================================================================
// 📁 File: src/components/SidebarLayout.tsx
// =============================================================================
// 🧭 SidebarLayout (CTO Recovery Prompt — v2026.02)
// Canonical role-based sidebar with tenantSlug-aware routing.
//
// Invariants (LOCKED):
// - PURE layout component (no auth redirects).
// - Auth enforcement lives ONLY in ProtectedRoute.
// - All routes prefixed with effective tenantSlug.
// - Role → menu mapping centralized in navItems.
// - TenantSlug resolution is URL-first and reserved-safe.
// - Logout clears session + tenant-safe redirect.
// - NO h-screen + overflow-hidden layout traps.
// =============================================================================

import React, { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  clearSession,
  getTenantSlugFromStorage,
  getUserFromToken,
} from "@/utils/auth";
import { Button } from "@/components/ui/button";
import { RuntimeTruthBanner } from "@/components/RuntimeTruthBanner";

interface SidebarLayoutProps {
  children: React.ReactNode;
}

enum UserRole {
  ADMIN = "admin",
  DDO = "ddo",
  PM = "pm",
  PC = "pc",
  FINANCE = "finance",
  MANAGEMENT = "management",
  CLIENT = "client",
  SUPER_ADMIN = "super_admin",
}

// -----------------------------------------------------------------------------
// Role-based navigation (role-rooted paths only)
// -----------------------------------------------------------------------------
const navItems = [
  // Admin
  { label: "Control Centre", path: "/admin/dashboard", roles: [UserRole.ADMIN] },
  { label: "Scholarship Setup", path: "/admin/dashboard#scholarship-setup", roles: [UserRole.ADMIN] },
  { label: "Users & Roles", path: "/admin/dashboard#users-roles", roles: [UserRole.ADMIN] },

  // DDO
  { label: "All Tenants", path: "/operations/overview", roles: [UserRole.DDO, UserRole.PM, UserRole.PC] },
  { label: "DDO Dashboard", path: "/ddo/dashboard", roles: [UserRole.DDO] },
  { label: "Submissions to Review", path: "/ddo/submissions", roles: [UserRole.DDO] },
  { label: "Applications Needing Attention", path: "/ddo/exceptions", roles: [UserRole.DDO] },

  // PM
  { label: "PM Dashboard", path: "/pm/dashboard", roles: [UserRole.PM] },
  { label: "Applications Needing Attention", path: "/pm/exceptions", roles: [UserRole.PM] },
  { label: "Scholarship Forms", path: "/pm/forms", roles: [UserRole.PM] },

  // PC
  { label: "PC Dashboard", path: "/pc/dashboard", roles: [UserRole.PC] },
  { label: "Exception Queue", path: "/pc/exceptions", roles: [UserRole.PC] },

  // Finance
  { label: "Finance Dashboard", path: "/finance/dashboard", roles: [UserRole.FINANCE] },
  { label: "Payment Issues", path: "/finance/exceptions", roles: [UserRole.FINANCE] },

  // Management
  { label: "Management Dashboard", path: "/management/dashboard", roles: [UserRole.MANAGEMENT] },

  // Client
  { label: "Client Dashboard", path: "/client/dashboard", roles: [UserRole.CLIENT] },

  // Super Admin
  { label: "Control Center", path: "/admin/tenant-settings", roles: [UserRole.SUPER_ADMIN] },
];

export const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { tenantSlug: routeTenantSlug } = useParams<{ tenantSlug: string }>();

  // ---------------------------------------------------------------------------
  // Layout can READ role but must NOT enforce auth
  // ---------------------------------------------------------------------------
  const user = getUserFromToken();
  const role = user?.role || "";

  // ---------------------------------------------------------------------------
  // Canonical Tenant Slug Resolution (URL-first + reserved-safe)
  // ---------------------------------------------------------------------------
  const RESERVED_SEGMENTS = new Set([
    "ddo",
    "pm",
    "pc",
    "finance",
    "management",
    "login",
    "403",
    "admin",
    "applicant",
    "client",
  ]);

  const sanitizeSlug = (value: string | null): string | null => {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed || RESERVED_SEGMENTS.has(trimmed)) return null;
    return trimmed;
  };

  const routeSlug = sanitizeSlug(routeTenantSlug || null);
  const storedSlug = sanitizeSlug(sessionStorage.getItem("tenant_slug"));
  const helperSlug = sanitizeSlug(getTenantSlugFromStorage());

  const effectiveSlug = routeSlug ?? storedSlug ?? helperSlug ?? "default";

  // ---------------------------------------------------------------------------
  // Role-based filtering
  // ---------------------------------------------------------------------------
  const navLinks = useMemo(
    () => navItems.filter((item) => item.roles.includes(role as UserRole)),
    [role]
  );

  // ---------------------------------------------------------------------------
  // Logout — tenant-safe redirect
  // ---------------------------------------------------------------------------
  const handleLogout = () => {
    clearSession();
    void navigate(`/${effectiveSlug}/login`, { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-muted/10">
      {/* --------------------------------------------------------------------- */}
      {/* Sidebar */}
      {/* --------------------------------------------------------------------- */}
      <aside className="w-64 shrink-0 bg-gray-900 text-white p-4 flex flex-col shadow-lg">
        <h2 className="text-lg font-semibold mb-6 capitalize">
          {role || "User"} Menu
        </h2>
        <div className="mb-4 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-xs">
          <div className="text-gray-400">Active workspace</div>
          <div className="mt-1 font-medium">{effectiveSlug}</div>
        </div>

        <nav className="flex-1">
          <ul className="space-y-2">
            {navLinks.map((item) => (
              <li key={item.path}>
                <Link
                  to={
                    item.path.startsWith("/operations")
                      ? item.path
                      : `/${effectiveSlug}${item.path}`
                  }
                  className="block px-3 py-2 rounded-md hover:bg-gray-700 transition-colors"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <Button
          variant="destructive"
          onClick={handleLogout}
          className="mt-6"
        >
          Logout
        </Button>
      </aside>

      {/* --------------------------------------------------------------------- */}
      {/* Main Content Area */}
      {/* - Vertical scroll only
          - Responsive padding
          - Prevent horizontal overflow
      */}
      <main className="min-w-0 max-w-[calc(100vw-16rem)] flex-1 overflow-x-hidden overflow-y-auto p-4 md:p-6">
        <RuntimeTruthBanner />
        {children}
      </main>
    </div>
  );
};

export default SidebarLayout;
